"use client";

import { createHash } from "crypto";

import { sendGTMEvent } from "@next/third-parties/google";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import {
  Auth, getAuth, inMemoryPersistence, User,
} from "firebase/auth";
import { initializeApp } from "firebase/app";

import config from "../services/config";
import { getAuthToken } from "../services/api";
import { replaceHttpOnlyAuthCookie, setAuthCookie } from "../services/auth";

interface AppUserType {
  token: string;
  email: string | undefined;
  uid: string;
  roles: string[];
  providers: string[];
  session: "PROVIDERS" | "DEEP_LINK"
}

// type for Login/Logout params
type AuthFlowParams = {
  path?: string;
  reason?: string;
  redirect?: string;
}

interface Props {
  user?: AppUserType | null;
  children: React.ReactNode;
}

interface AuthContextType {
  user?: AppUserType;
  firebaseUser?: User;
  auth?: Auth;
  signup: (authFlowParams?: Pick<AuthFlowParams, "path" | "redirect">) => void;
  login: (authFlowParams?: AuthFlowParams) => void;
  logout: (authFlowParams?: AuthFlowParams) => Promise<void>;
  onLoginPassed: (token: string, params?: { rememberMe?: boolean, redirect?: string }) => Promise<void>;
}

const AuthContext = React.createContext<AuthContextType>({
  user: undefined,
  firebaseUser: undefined,
  auth: undefined,
  signup: () => undefined,
  login: () => undefined,
  logout: () => Promise.resolve(),
  onLoginPassed: async (token: string, params?: { rememberMe?: boolean, redirect?: string }) => Promise.resolve(),
});

const AuthProvider: React.FC<Props> = ({ user, children }) => {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [httpOnlyChecked, setHttpOnlyChecked] = useState(false);

  const auth = useMemo(() => {
    const app = initializeApp(config.gcp);
    const auth = getAuth(app);
    auth.setPersistence(inMemoryPersistence);
    auth.useDeviceLanguage();
    return auth;
  }, []);

  const getAuthCookie = () => {
    const cookies = document.cookie?.split("; ") || [];
    const authCookie = cookies.find((c) => c.startsWith("auth-session="));
    return authCookie?.replace("auth-session=", "") || undefined;
  };

  const [firebaseUser, setFirebaseUser] = useState<User | null>(auth.currentUser);

  const signup = (authFlowParams?: Pick<AuthFlowParams, "path" | "redirect">) => {
    const redirectUrl = (() => {
      const host = `${window.location.protocol}//${window.location.host}`;
      if (authFlowParams?.redirect && authFlowParams.redirect.startsWith(host)
        && !authFlowParams.redirect.includes("/register")) return authFlowParams.redirect;
      const r = `${host}${pathname}`;
      if (r.includes("/register")) return host;
      return r;
    })();
    const params = new URLSearchParams({
      redirect: redirectUrl,
    });
    const stringParams = params.toString();
    router.push(`${authFlowParams?.path || "/register"}${stringParams?.length > 0 ? `?${stringParams}` : ""}`);
  };

  const login = (authFlowParams?: AuthFlowParams) => {
    const redirectUrl = (() => {
      const host = `${window.location.protocol}//${window.location.host}`;
      if (authFlowParams?.redirect && authFlowParams.redirect.startsWith(host)
        && !authFlowParams.redirect.includes("/login")) return authFlowParams.redirect;
      const r = `${host}${pathname}`;
      if (r.includes("/login")) return host;
      return r;
    })();
    const params = new URLSearchParams({
      ...(authFlowParams?.reason ? { reason: authFlowParams.reason } : {}),
      redirect: redirectUrl,
    });
    const stringParams = params.toString();
    router.push(`${authFlowParams?.path || "/login"}${stringParams?.length > 0 ? `?${stringParams}` : ""}`);
  };

  const logout = async (authFlowParams?: AuthFlowParams) => {
    await fetch("/api/auth/logout", {
      method: "GET",
    });
    const params = new URLSearchParams({
      ...(authFlowParams?.reason ? { reason: authFlowParams.reason } : {}),
      ...(authFlowParams?.redirect ? { redirect: authFlowParams.redirect } : {}),
    });
    const stringParams = params.toString();
    if (window?.location?.href) window.location.href = `${authFlowParams?.path || "/"}${stringParams?.length > 0 ? `?${stringParams}` : ""}`;
  };

  const onLoginPassed = async (token: string, params?: { rememberMe?: boolean, redirect?: string}) => {
    const r = searchParams.get("redirect") || params?.redirect || "/";
    try {
      const res = await getAuthToken({ variables: { idToken: token, rememberMe: params?.rememberMe } });
      if (res.data?.getAuthToken?.authToken) {
        await setAuthCookie(res.data.getAuthToken.authToken);
        // await auth.signOut();
        if (r) router.push(r || "/");
      }
    } catch (e) { /* handle error */ }
  };

  useEffect(() => {
    if (!httpOnlyChecked) return;
    if (user && !getAuthCookie() && !pathname.includes("/login")) {
      logout({ path: "/login", reason: "EXPIRED_SESSION", redirect: pathname });
    }
  }, [pathname, user, httpOnlyChecked]);

  useEffect(() => {
    if (user) {
      try {
        sendGTMEvent({ user_id: user.uid });
        if (user?.email) sendGTMEvent({ user_data: { sha256_email_address: createHash("sha256").update(user.email).digest("hex") } });
        if (window?.clarity) window.clarity("set", "userUid", user.uid);
      } catch (error) { /* */ }
    } else {
      sendGTMEvent({ user_id: null });
      sendGTMEvent({ user_data: null });
    }
  }, [user]);

  useEffect(() => {
    auth.onAuthStateChanged((u) => setFirebaseUser(u));
  }, [auth]);

  useEffect(() => {
    // notify sentry
    if (user) {
      Sentry.setUser({ id: user.uid });
    } else {
      Sentry.setUser(null);
    }
  }, [user?.uid]);

  // Fix for cookie bug
  useEffect(() => {
    const authCookieExistsOnClient = document.cookie.includes("auth-session=");
    if (user && !authCookieExistsOnClient) {
      (async () => {
        await replaceHttpOnlyAuthCookie();
        setHttpOnlyChecked(true);
      })();
    } else {
      setHttpOnlyChecked(true);
    }
  }, [user, pathname]);

  const defaultValue = useMemo(
    () => ({
      user: user || undefined,
      firebaseUser: firebaseUser || undefined,
      auth,
      signup,
      login,
      onLoginPassed,
      logout: () => logout().then(() => router.push("/")),
    }),
    [config, pathname, user, firebaseUser],
  );

  return (
    <AuthContext.Provider value={defaultValue}>
      {user?.session === "DEEP_LINK" && <div className="bg-red-600 text-center text-white">Impersonate session</div>}
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => React.useContext(AuthContext);

export default AuthContext;
export { AuthProvider, useAuth };
export type { AppUserType };
